<template>
    <component
        :is="field.layout"
        v-bind="passedData"
        @back="onBack"
        @change="onChange"
        @verify="onVerify"
        @submit="onSubmit"
        @delegate="onDelegate"
    >
        <component
            :is="field.form"
            v-bind="passedData"
            @back="onBack"
            @change="onChange"
            @verify="onVerify"
            @submit="onSubmit"
            @delegate="onDelegate"
        >
            <component
                :is="field.component"
                v-bind="passedData"
                @back="onBack"
                @change="onChange"
                @verify="onVerify"
                @submit="onSubmit"
                @delegate="onDelegate"
            />
        </component>
        <template v-if="shouldDisplayContactFooter" #contactFooter>
            <Footer :title="question.metadata.title" :licenses="smartAttributeLicenses" />
        </template>
    </component>
</template>

<script lang="ts">
    import { Vue, Component, Prop } from 'vue-property-decorator';

    import Question from '@evidentid/ido-lib/interfaces/Question';

    import LicenseQuestionContactFooter from '@/components/common/Footer/LicenseQuestionContactFooter.vue';
    import Field from '@/fields/Field';
    import License from '@/interfaces/licenses/License';
    import { Status, VerificationFailureReason, VerificationResult } from '@/store/interfaces/IdoState';
    import { useIdoStore } from '@/store/store';
    import { licenseStateNamesWithCustomValidation } from '@/utils/licenseValidation/constants';

    @Component({
        components: { Footer: LicenseQuestionContactFooter },
    })
    export default class QuestionView extends Vue {
        @Prop(Object)
        private question!: Question;

        @Prop(Object)
        private field!: Field;

        private get passedData() {
            const { field, value, question } = this;
            return {
                field,
                question,
                value,
                valid: this.valid,
                submitting: this.submitting,
                questionsLeft: this.questionsLeft,
                delegationStatus: this.delegationStatus,
                delegationUrl: this.delegationUrl,
                delegationExpiresAt: this.delegationExpiresAt,
                verificationResult: this.verificationResult,
                verificationFailureReason: this.verificationFailureReason,
                verificationResolutionAttempts: this.verificationResolutionAttempts,
                verificationsFailed: this.verificationsFailed,
            };
        }

        private get questionsLeft() {
            return useIdoStore().questions
                .reduce((acc, question) => (question.complete ? acc : acc + 1), 0);
        }

        private get value() {
            const { field, question } = this;
            const value = useIdoStore().values[question.attrType];
            return value == null && field ? field.getDefaultValue(question) : value;
        }

        private get submitting() {
            return useIdoStore().submission[this.question.attrType];
        }

        private get delegationUrl() {
            const delegation = useIdoStore().delegationsRequested[this.question.attrType];
            return delegation ? delegation.url || null : null;
        }

        private get delegationExpiresAt() {
            const delegation = useIdoStore().delegationsRequested[this.question.attrType];
            return delegation ? delegation.expiresAt ?? null : null;
        }

        private get delegationStatus() {
            const delegation = useIdoStore().delegationsRequested[this.question.attrType];
            return delegation ? delegation.status : Status.uninitialized;
        }

        private get verification() {
            return useIdoStore().verificationsRequested[this.question.attrType];
        }

        private get verificationResolutionAttempts() {
            const verification = this.verification;
            return verification?.resolutionAttempts || 0;
        }

        private get verificationResult() {
            const verification = this.verification;
            return verification ? verification.result : VerificationResult.uninitialized;
        }

        private get verificationFailureReason() {
            const verification = this.verification;
            return verification?.result === VerificationResult.failed
                ? verification.failureReason || VerificationFailureReason.unknown
                : null;
        }

        private get verificationsFailed() {
            const verification = this.verification;
            return verification?.failures || 0;
        }

        private get valid() {
            return this.field.isValid(this.question, this.value);
        }

        private get shouldDisplayContactFooter(): boolean {
            return licenseStateNamesWithCustomValidation.some((name) => this.question.type === `smart-attribute:${name}`);
        }

        private get smartAttributeLicenses(): License[] {
            return this.value?.licenses ?? [];
        }

        private onChange(value: any) {
            return useIdoStore().changeValue({
                name: this.question.attrType,
                value,
            });
        }

        private async onSubmit(done: () => void, onUploadProgress?: (progress: number) => void) {
            const { field, question, value } = this;

            if (!field.isValid(question, value)) {
                return;
            }

            await useIdoStore().submitAttributes({
                attributes: await field.encode(question, value),
                onUploadProgress,
            });

            if (typeof done === 'function') {
                done();
            }
        }

        private async onVerify(done: () => void, onUploadProgress?: (progress: number) => void) {
            const { field, question, value } = this;

            if (!field.isValid(question, value)) {
                return;
            }

            await useIdoStore().verifyAttributes({
                attributes: await field.encode(question, value),
                onUploadProgress,
            });

            if (typeof done === 'function') {
                done();
            }
        }

        private onDelegate() {
            useIdoStore().delegate(this.question.attrType);
        }

        private onBack() {
            this.$router.push({ name: 'overview' });
        }
    }
</script>
